import React, { useMemo, useState } from 'react';

import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import {
  type MarketingRouter,
  type MarketingRouterExperienceResult,
  type MarketingRouterMatchInput,
  evaluateMarketingRouter,
} from '@luxuryescapes/contract-svc-promo/marketing-router/client';
import { getRegions } from '@luxuryescapes/lib-regions';

import DebugModal from '~/components/DebugModal/DebugModal';

import { useMarketingRouters } from '~/hooks/apis/promo/useMarketingRouterRules';

import type { MarketingRouterQuery } from '~/services/PromoService';

type InputField = {
  key: keyof Pick<
    MarketingRouterMatchInput,
    'utmSource' | 'utmMedium' | 'utmCampaign' | 'utmTerm' | 'utmContent' | 'url'
  >;
  configKey: 'utm_source' | 'utm_medium' | 'utm_campaign' | 'utm_term' | 'utm_content' | 'url';
  label: string;
};

const INPUT_FIELDS: Array<InputField> = [
  { key: 'utmSource', configKey: 'utm_source', label: 'UTM source' },
  { key: 'utmMedium', configKey: 'utm_medium', label: 'UTM medium' },
  { key: 'utmCampaign', configKey: 'utm_campaign', label: 'UTM campaign' },
  { key: 'utmTerm', configKey: 'utm_term', label: 'UTM term' },
  { key: 'utmContent', configKey: 'utm_content', label: 'UTM content' },
  { key: 'url', configKey: 'url', label: 'URL' },
];

type EvaluationInput = Record<InputField['key'], string> & { region: string };

const EMPTY_INPUT: EvaluationInput = {
  region: '',
  utmSource: '',
  utmMedium: '',
  utmCampaign: '',
  utmTerm: '',
  utmContent: '',
  url: '',
};

const EMPTY_MARKETING_ROUTERS: Array<MarketingRouter> = [];

type MarketingRouterEvaluationRule = NonNullable<
  NonNullable<MarketingRouterExperienceResult['evaluation']['selected']>['rule']
>;

function formatMatchCriteria(match: MarketingRouterEvaluationRule['match']) {
  const criteria = Object.entries(match).filter(([, values]) => (values ?? []).length > 0);

  if (criteria.length === 0) {
    return 'All inputs';
  }

  // A field passes on any one of its values, so show them as alternatives.
  return criteria.map(([key, values]) => `${key}: ${(values ?? []).join(' | ')}`).join(', ');
}

function formatVariants(variants: MarketingRouterEvaluationRule['variants']) {
  return variants
    .map(
      (variant) =>
        `${variant.variant_name} (${variant.mrvid}; weight ${variant.weight}; ${variant.is_control ? 'control' : 'treatment'})`,
    )
    .join(', ');
}

function formatTreatmentDetails(treatment: MarketingRouterExperienceResult['treatments'][number]) {
  const { applyOrder: _applyOrder, mrid: _mrid, mrvid: _mrvid, ruleId: _ruleId, type: _type, ...details } = treatment;
  const entries = Object.entries(details);

  return entries.length > 0 ? entries.map(([key, value]) => `${key}: ${value}`).join(', ') : '-';
}

function configuredValues(marketingRouters: Array<MarketingRouter>, configKey: InputField['configKey']): Array<string> {
  return Array.from(
    new Set(
      marketingRouters.flatMap((router) =>
        router.rule_json.flatMap((rule) => rule.match[configKey] ?? []).filter(Boolean),
      ),
    ),
  ).sort((first, second) => first.localeCompare(second));
}

function getConfigurationWarnings(marketingRouters: Array<MarketingRouter>): Array<string> {
  return marketingRouters.flatMap((router) =>
    router.rule_json.flatMap((rule) => {
      if (rule.variants.some((variant) => variant.weight > 0)) {
        return [];
      }

      return [`${router.mrid} / ${rule.name} has no variant with a positive weight.`];
    }),
  );
}

function MarketingRouterEvaluator({ tenant }: { tenant: App.Tenant }) {
  const query: MarketingRouterQuery = { brand: tenant.brand as MarketingRouterQuery['brand'], status: 'active' };
  const routersQuery = useMarketingRouters({ query, enabled: false });
  const [hasLoadedClientInit, setHasLoadedClientInit] = useState(false);
  const marketingRouters = hasLoadedClientInit
    ? (routersQuery.data ?? EMPTY_MARKETING_ROUTERS)
    : EMPTY_MARKETING_ROUTERS;
  const [input, setInput] = useState<EvaluationInput>(EMPTY_INPUT);
  const [result, setResult] = useState<MarketingRouterExperienceResult | null>(null);

  const regions = useMemo(
    () =>
      getRegions(tenant.brand)
        .map((region) => ({ code: region.code, name: region.name }))
        .sort((first, second) => first.name.localeCompare(second.name)),
    [tenant.brand],
  );
  const warnings = useMemo(() => getConfigurationWarnings(marketingRouters), [marketingRouters]);

  const updateInput = (key: keyof EvaluationInput, value: string) => {
    setInput((current) => ({ ...current, [key]: value }));
    setResult(null);
  };

  const loadClientInit = async () => {
    const response = await routersQuery.refetch();

    if (!response.error) {
      setHasLoadedClientInit(true);
      setResult(null);
    }
  };

  const runEvaluation = () => {
    const evaluationInput: MarketingRouterMatchInput = {
      utmSource: input.utmSource || undefined,
      utmMedium: input.utmMedium || undefined,
      utmCampaign: input.utmCampaign || undefined,
      utmTerm: input.utmTerm || undefined,
      utmContent: input.utmContent || undefined,
      url: input.url || undefined,
    };
    const routersForRegion = input.region
      ? marketingRouters.filter((router) => router.region == null || router.region === input.region)
      : marketingRouters;

    setResult(evaluateMarketingRouter(routersForRegion, evaluationInput, { includeEvaluationsInResult: true }));
  };

  return (
    <Card>
      <CardContent>
        <Stack spacing={2}>
          <Box>
            <Typography variant="h6">Marketing Router evaluator</Typography>
            <Typography color="text.secondary" variant="body2">
              Load the active client init snapshot, then run the exported client evaluator against that configuration.
            </Typography>
          </Box>

          <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 2, bgcolor: 'background.paper' }}>
            <Stack spacing={1.5}>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  1. Client init
                </Typography>
                <Typography color="text.secondary" variant="body2">
                  Load the active Marketing Router configuration for {tenant.brand} before evaluating it.
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Button variant="text" disabled={routersQuery.isFetching} onClick={() => void loadClientInit()}>
                  {routersQuery.isFetching ? 'Loading…' : `Load ${tenant.brand} client init`}
                </Button>
                {hasLoadedClientInit && routersQuery.data ? (
                  <DebugModal
                    type="generic"
                    title="Client init loaded"
                    data={routersQuery.data}
                    hideIcon
                    buttonProps={{ size: 'small', variant: 'text' }}
                  >
                    Inspect client init
                  </DebugModal>
                ) : null}
              </Stack>
              {routersQuery.isError ? (
                <Alert severity="error" variant="outlined">
                  {routersQuery.error instanceof Error
                    ? routersQuery.error.message
                    : 'Failed to load Marketing Router client init.'}
                </Alert>
              ) : null}
            </Stack>
          </Box>

          {!hasLoadedClientInit ? (
            <Alert severity="info" variant="outlined">
              Load client init to configure the evaluator.
            </Alert>
          ) : null}
          {hasLoadedClientInit && !routersQuery.isError && marketingRouters.length === 0 ? (
            <Alert severity="warning">No active Marketing Router configuration is available for this tenant.</Alert>
          ) : null}
          {warnings.map((warning) => (
            <Alert key={warning} severity="warning">
              Missing configuration: {warning}
            </Alert>
          ))}

          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              2. Evaluation input
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={2}>
              <Autocomplete
                freeSolo
                options={regions}
                getOptionLabel={(region) => (typeof region === 'string' ? region : `${region.name} (${region.code})`)}
                inputValue={input.region}
                onChange={(_event, region) =>
                  updateInput('region', typeof region === 'string' ? region : (region?.code ?? ''))
                }
                onInputChange={(_event, value) => updateInput('region', value)}
                sx={{ minWidth: 240 }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Region"
                    helperText="Choose a region or enter a region code."
                    size="small"
                  />
                )}
              />
              {INPUT_FIELDS.map((field) => {
                const values = configuredValues(marketingRouters, field.configKey);

                return (
                  <Autocomplete
                    key={field.key}
                    freeSolo
                    options={values}
                    inputValue={input[field.key]}
                    onInputChange={(_event, value) => updateInput(field.key, value)}
                    sx={{ minWidth: 180 }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={field.label}
                        helperText={
                          values.length === 0 ? 'Enter a value to test.' : 'Choose a configured value or enter one.'
                        }
                        size="small"
                      />
                    )}
                  />
                );
              })}
            </Stack>
          </Box>

          <Box>
            <Button
              disabled={!hasLoadedClientInit || marketingRouters.length === 0}
              variant="contained"
              onClick={runEvaluation}
            >
              Run evaluation
            </Button>
          </Box>

          {result ? (
            <Stack spacing={1.5}>
              <Alert severity={result.matched ? 'success' : 'info'}>
                {result.matched
                  ? 'A matching Marketing Router rule was found.'
                  : 'No Marketing Router rules matched these inputs.'}
              </Alert>
              {result.matched ? (
                <Stack spacing={1.5}>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                      Evaluation candidates
                    </Typography>
                    <Box sx={{ overflowX: 'auto' }}>
                      <Table size="small" aria-label="Marketing Router evaluation candidates">
                        <TableHead>
                          <TableRow>
                            <TableCell>Router ID</TableCell>
                            <TableCell>Rule ID</TableCell>
                            <TableCell>Rule</TableCell>
                            <TableCell>Matched</TableCell>
                            <TableCell>Reason</TableCell>
                            <TableCell>Match criteria</TableCell>
                            <TableCell>Rule window</TableCell>
                            <TableCell>Variants</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {result.evaluation.candidates.map((candidate) => (
                            <TableRow key={`${candidate.mrid}-${candidate.rule?.id ?? 'no-rule'}`}>
                              <TableCell>{candidate.mrid}</TableCell>
                              <TableCell>{candidate.rule?.id ?? '-'}</TableCell>
                              <TableCell>{candidate.rule?.name ?? '-'}</TableCell>
                              <TableCell>{candidate.matched ? 'Yes' : 'No'}</TableCell>
                              <TableCell>{candidate.reason ?? '-'}</TableCell>
                              <TableCell>{candidate.rule ? formatMatchCriteria(candidate.rule.match) : '-'}</TableCell>
                              <TableCell>
                                {candidate.rule ? `${candidate.rule.start_date} to ${candidate.rule.end_date}` : '-'}
                              </TableCell>
                              <TableCell>{candidate.rule ? formatVariants(candidate.rule.variants) : '-'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Box>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                      Emitted treatments
                    </Typography>
                    <Box sx={{ overflowX: 'auto' }}>
                      <Table size="small" aria-label="Marketing Router evaluation treatments">
                        <TableHead>
                          <TableRow>
                            <TableCell>Router ID</TableCell>
                            <TableCell>Rule ID</TableCell>
                            <TableCell>Variant ID</TableCell>
                            <TableCell>Treatment</TableCell>
                            <TableCell>Apply order</TableCell>
                            <TableCell>Details</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {result.treatments.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={6}>The matching rule did not emit any client treatments.</TableCell>
                            </TableRow>
                          ) : (
                            result.treatments.map((treatment, index) => (
                              <TableRow key={`${treatment.mrid}-${treatment.ruleId}-${treatment.mrvid}-${index}`}>
                                <TableCell>{treatment.mrid}</TableCell>
                                <TableCell>{treatment.ruleId}</TableCell>
                                <TableCell>{treatment.mrvid}</TableCell>
                                <TableCell>{treatment.type}</TableCell>
                                <TableCell>{treatment.applyOrder ?? '-'}</TableCell>
                                <TableCell>{formatTreatmentDetails(treatment)}</TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </Box>
                  </Box>
                </Stack>
              ) : null}
              <DebugModal
                buttonProps={{ variant: 'outlined', sx: { alignSelf: 'flex-start' } }}
                data={result}
                title="Debug evaluation"
                type="generic"
              />
            </Stack>
          ) : null}
        </Stack>
      </CardContent>
    </Card>
  );
}

export default MarketingRouterEvaluator;
